import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useSearchParams } from "react-router-dom";
import { api, userStore } from "@/lib/api";
import { COUNTRIES, TIMEZONES, LANGUAGES, FEEDBACK_TYPES } from "@/lib/profileData";
import { User, Lock, Globe, CalendarDays, MessageSquare, Check, AlertCircle, Palette, Save, Mail, Phone, Briefcase, MapPin, Plus, Trash2, Eye, EyeOff, ShieldAlert, Bell } from "lucide-react";
import { useTheme } from "@/lib/theme";
import { ThemePickerModal } from "@/components/ThemeButton";
import NotificationsPanel from "@/components/NotificationsPanel";

const SECTIONS = [
  { id: "profile", label: "Profile Information", icon: User },
  { id: "password", label: "Change Password", icon: Lock },
  { id: "regional", label: "Regional Settings", icon: Globe },
  { id: "holidays", label: "Holiday Selection", icon: CalendarDays },
  { id: "appearance", label: "Appearance", icon: Palette },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "feedback", label: "Send Feedback", icon: MessageSquare },
];

function Toast({ kind = "success", children }) {
  if (!children) return null;
  const cls = kind === "success"
    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
    : "bg-red-50 border-red-200 text-red-700";
  return (
    <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm ${cls}`} data-testid={`toast-${kind}`}>
      {kind === "success" ? <Check className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
      <span>{children}</span>
    </div>
  );
}

export default function ProfilePage({ embedded = false }) {
  const { user } = useAuth();
  const { current } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const forcePassword = searchParams.get("force") === "password" || !!user?.password_must_change;
  const [section, setSection] = useState(forcePassword ? "password" : "profile");
  const [themeOpen, setThemeOpen] = useState(false);

  // Profile section
  const [profile, setProfile] = useState({ full_name: "", phone: "", specialty: "", address: "" });
  const [pSaving, setPSaving] = useState(false);
  const [pMsg, setPMsg] = useState({ kind: "", text: "" });

  // Password section
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState({ kind: "", text: "" });
  const [pwShow, setPwShow] = useState({ current: false, next: false, confirm: false });

  // Preferences
  const [prefs, setPrefs] = useState({
    language: "en-US",
    country: "USA",
    timezone: "America/New_York",
    holiday_enforcement: true,
    selected_holiday_names: [],
    custom_holidays: [],
  });
  const [prefSaving, setPrefSaving] = useState(false);
  const [prefMsg, setPrefMsg] = useState({ kind: "", text: "" });

  // Holiday calendar (loaded from backend)
  const [holidayCalendar, setHolidayCalendar] = useState(null);
  const [customForm, setCustomForm] = useState({ name: "", date: "", repeat_yearly: false });
  const [holidayDirty, setHolidayDirty] = useState(false);
  const [holidayMsg, setHolidayMsg] = useState({ kind: "", text: "" });

  // Feedback
  const [fb, setFb] = useState({ feedback_type: "Feature Request", subject: "", message: "" });
  const [fbSaving, setFbSaving] = useState(false);
  const [fbMsg, setFbMsg] = useState({ kind: "", text: "" });

  useEffect(() => {
    if (user) {
      setProfile({
        full_name: user.full_name || "",
        phone: user.phone || "",
        specialty: user.specialty || "",
        address: user.address || "",
      });
    }
    api.get("/auth/preferences").then((r) => setPrefs((p) => ({ ...p, ...r.data }))).catch(() => {});
    api.get("/holidays/calendar").then((r) => setHolidayCalendar(r.data)).catch(() => {});
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setPSaving(true); setPMsg({ kind: "", text: "" });
    try {
      const r = await api.patch("/auth/me", profile);
      userStore.set(r.data);
      setPMsg({ kind: "success", text: "Profile updated successfully." });
    } catch (e) {
      setPMsg({ kind: "error", text: e.response?.data?.detail || "Failed to update profile." });
    } finally { setPSaving(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPwMsg({ kind: "", text: "" });
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwMsg({ kind: "error", text: "New password and confirmation don't match." });
      return;
    }
    if (pwForm.new_password.length < 8) {
      setPwMsg({ kind: "error", text: "New password must be at least 8 characters." });
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      setPwMsg({ kind: "success", text: "Password updated. Use it next time you sign in." });
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
      // Refresh the user record so the password_must_change flag drops, and
      // bounce the user out of the forced-change mode (clears ?force=password).
      try {
        const me = await api.get("/auth/me");
        userStore.set(me.data);
      } catch (_) { /* non-fatal */ }
      if (forcePassword) {
        setSearchParams({}, { replace: true });
      }
    } catch (e) {
      setPwMsg({ kind: "error", text: e.response?.data?.detail || "Failed to update password." });
    } finally { setPwSaving(false); }
  };

  const savePrefs = async (patch) => {
    setPrefSaving(true); setPrefMsg({ kind: "", text: "" });
    try {
      const merged = { ...prefs, ...patch };
      setPrefs(merged);
      const r = await api.patch("/auth/preferences", patch);
      setPrefs(r.data);
      setPrefMsg({ kind: "success", text: "Saved." });
      setTimeout(() => setPrefMsg({ kind: "", text: "" }), 2000);
    } catch (e) {
      setPrefMsg({ kind: "error", text: e.response?.data?.detail || "Failed to save." });
    } finally { setPrefSaving(false); }
  };

  const toggleHoliday = (name) => {
    setHolidayDirty(true);
    setPrefs((p) => {
      const cur = p.selected_holiday_names || [];
      const next = cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name];
      return { ...p, selected_holiday_names: next };
    });
  };

  const setEnforcement = (v) => {
    setHolidayDirty(true);
    setPrefs((p) => ({ ...p, holiday_enforcement: v }));
  };

  const addCustomHoliday = () => {
    if (!customForm.name.trim() || !customForm.date) return;
    setHolidayDirty(true);
    setPrefs((p) => ({
      ...p,
      custom_holidays: [
        ...(p.custom_holidays || []),
        { id: `${Date.now()}`, name: customForm.name.trim(), date: customForm.date, repeat_yearly: customForm.repeat_yearly },
      ],
    }));
    setCustomForm({ name: "", date: "", repeat_yearly: false });
  };

  const removeCustomHoliday = (id) => {
    setHolidayDirty(true);
    setPrefs((p) => ({ ...p, custom_holidays: (p.custom_holidays || []).filter((c) => c.id !== id) }));
  };

  const saveHolidayPrefs = async () => {
    setHolidayMsg({ kind: "", text: "" });
    try {
      await api.patch("/auth/preferences", {
        holiday_enforcement: prefs.holiday_enforcement,
        selected_holiday_names: prefs.selected_holiday_names || [],
        custom_holidays: prefs.custom_holidays || [],
      });
      setHolidayMsg({ kind: "success", text: "Holiday preferences saved." });
      setHolidayDirty(false);
      setTimeout(() => setHolidayMsg({ kind: "", text: "" }), 2500);
    } catch (e) {
      setHolidayMsg({ kind: "error", text: e.response?.data?.detail || "Failed to save." });
    }
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!fb.message.trim()) return;
    setFbSaving(true); setFbMsg({ kind: "", text: "" });
    try {
      await api.post("/feedback", fb);
      setFbMsg({ kind: "success", text: "Thank you — your feedback has been sent to the team." });
      setFb({ feedback_type: "Feature Request", subject: "", message: "" });
    } catch (e) {
      setFbMsg({ kind: "error", text: e.response?.data?.detail || "Failed to send feedback." });
    } finally { setFbSaving(false); }
  };

  const inputCls = "w-full px-3 py-2.5 rounded-lg border border-stone-300 bg-white focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none";

  return (
    <div className={embedded ? "" : "p-8 max-w-[1200px]"} data-testid="profile-page">
      {!embedded && (
        <div className="mb-6">
          <h1 className="font-display text-3xl font-bold text-stone-900">My Profile</h1>
          <p className="text-stone-500 mt-1">Manage your account, security, regional preferences and feedback.</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-6">
        {/* Side nav */}
        <aside>
          <div className="bg-white border border-stone-200 rounded-xl shadow-soft p-3" data-testid="profile-section-nav">
            {SECTIONS.map((s) => {
              const locked = forcePassword && s.id !== "password";
              return (
                <button key={s.id}
                  onClick={() => { if (!locked) setSection(s.id); }}
                  disabled={locked}
                  data-testid={`profile-section-${s.id}`}
                  title={locked ? "Please change your temporary password to access other sections." : undefined}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                    section === s.id ? "bg-brand-50 text-brand-700" : locked ? "text-stone-300 cursor-not-allowed" : "text-stone-600 hover:bg-stone-50"
                  }`}
                >
                  <s.icon className="w-4 h-4" />{s.label}
                </button>
              );
            })}
          </div>
        </aside>

        {/* Section content */}
        <div className="space-y-4">
          {section === "profile" && (
            <form onSubmit={saveProfile} className="bg-white border border-stone-200 rounded-xl shadow-soft p-6 space-y-4" data-testid="section-profile">
              <div className="flex items-center gap-4 pb-4 border-b border-stone-100">
                <div className="w-14 h-14 rounded-full bg-brand-100 text-brand-700 font-display font-bold text-lg flex items-center justify-center">
                  {(user?.full_name || "?").split(" ").map(s=>s[0]).slice(0,2).join("").toUpperCase()}
                </div>
                <div>
                  <h2 className="font-display font-semibold text-stone-900 text-lg">{user?.full_name}</h2>
                  <div className="text-xs text-stone-500 capitalize">{user?.role}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">Full name</label>
                  <input data-testid="profile-fullname-input" required value={profile.full_name} onChange={(e)=>setProfile({...profile, full_name:e.target.value})} className={inputCls} />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-stone-400" />Email <span className="text-xs text-stone-400">(read-only)</span></label>
                  <input value={user?.email || ""} readOnly className={`${inputCls} bg-stone-50 text-stone-500`} />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-stone-400" />Phone</label>
                  <input data-testid="profile-phone-input" value={profile.phone} onChange={(e)=>setProfile({...profile, phone:e.target.value})} className={inputCls} />
                </div>
                {user?.role !== "patient" && (
                  <div>
                    <label className="text-sm font-medium text-stone-700 mb-1.5 flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-stone-400" />Specialty</label>
                    <input data-testid="profile-specialty-input" value={profile.specialty} onChange={(e)=>setProfile({...profile, specialty:e.target.value})} className={inputCls} />
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-stone-700 mb-1.5 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-stone-400" />Address</label>
                  <input data-testid="profile-address-input" value={profile.address} onChange={(e)=>setProfile({...profile, address:e.target.value})} className={inputCls} />
                </div>
              </div>

              {pMsg.text && <Toast kind={pMsg.kind}>{pMsg.text}</Toast>}
              <div className="flex justify-end pt-2">
                <button data-testid="profile-save-btn" disabled={pSaving} type="submit" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold disabled:opacity-60">
                  <Save className="w-4 h-4" />{pSaving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </form>
          )}

          {section === "password" && (
            <form onSubmit={savePassword} className="bg-white border border-stone-200 rounded-xl shadow-soft p-6 space-y-4 max-w-xl" data-testid="section-password">
              {forcePassword && (
                <div
                  className="flex items-start gap-2.5 px-3.5 py-3 rounded-lg border bg-amber-50 border-amber-300 text-amber-900"
                  data-testid="must-change-banner"
                >
                  <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="text-sm leading-relaxed">
                    <b>You're signed in with a temporary password.</b> Please set a new password before continuing — other sections are temporarily locked.
                  </div>
                </div>
              )}
              <div>
                <h2 className="font-display font-semibold text-stone-900 text-lg">Change password</h2>
                <p className="text-sm text-stone-500 mt-1">Use at least 8 characters. Mix letters, numbers and symbols for the strongest security.</p>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Current password</label>
                <div className="relative">
                  <input data-testid="pw-current" type={pwShow.current ? "text" : "password"} required value={pwForm.current_password} onChange={(e)=>setPwForm({...pwForm, current_password:e.target.value})} className={`${inputCls} pr-10`} />
                  <button
                    type="button"
                    data-testid="pw-current-toggle"
                    onClick={() => setPwShow((s) => ({ ...s, current: !s.current }))}
                    aria-label={pwShow.current ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-stone-500 hover:text-stone-700"
                  >
                    {pwShow.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">New password</label>
                <div className="relative">
                  <input data-testid="pw-new" type={pwShow.next ? "text" : "password"} required minLength={8} value={pwForm.new_password} onChange={(e)=>setPwForm({...pwForm, new_password:e.target.value})} className={`${inputCls} pr-10`} />
                  <button
                    type="button"
                    data-testid="pw-new-toggle"
                    onClick={() => setPwShow((s) => ({ ...s, next: !s.next }))}
                    aria-label={pwShow.next ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-stone-500 hover:text-stone-700"
                  >
                    {pwShow.next ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Confirm new password</label>
                <div className="relative">
                  <input data-testid="pw-confirm" type={pwShow.confirm ? "text" : "password"} required minLength={8} value={pwForm.confirm_password} onChange={(e)=>setPwForm({...pwForm, confirm_password:e.target.value})} className={`${inputCls} pr-10`} />
                  <button
                    type="button"
                    data-testid="pw-confirm-toggle"
                    onClick={() => setPwShow((s) => ({ ...s, confirm: !s.confirm }))}
                    aria-label={pwShow.confirm ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-stone-500 hover:text-stone-700"
                  >
                    {pwShow.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {pwMsg.text && <Toast kind={pwMsg.kind}>{pwMsg.text}</Toast>}
              <div className="flex justify-end pt-2">
                <button data-testid="pw-save-btn" disabled={pwSaving} type="submit" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold disabled:opacity-60">
                  <Lock className="w-4 h-4" />{pwSaving ? "Updating…" : "Update password"}
                </button>
              </div>
            </form>
          )}

          {section === "regional" && (
            <div className="bg-white border border-stone-200 rounded-xl shadow-soft p-6 space-y-4 max-w-xl" data-testid="section-regional">
              <div>
                <h2 className="font-display font-semibold text-stone-900 text-lg">Regional settings</h2>
                <p className="text-sm text-stone-500 mt-1">Choose your language, country and time zone. Calendar times will display in your selected zone.</p>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Language</label>
                <select data-testid="pref-language" value={prefs.language} onChange={(e)=>savePrefs({language:e.target.value})} className={inputCls}>
                  {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
                <p className="text-xs text-stone-400 mt-1">More languages coming soon.</p>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Country</label>
                <select data-testid="pref-country" value={prefs.country} onChange={(e)=>savePrefs({country:e.target.value})} className={inputCls}>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Time zone</label>
                <select data-testid="pref-timezone" value={prefs.timezone} onChange={(e)=>savePrefs({timezone:e.target.value})} className={inputCls}>
                  {TIMEZONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <p className="text-xs text-stone-400 mt-1">Detected browser zone: {Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
              </div>
              {prefMsg.text && <Toast kind={prefMsg.kind}>{prefMsg.text}</Toast>}
            </div>
          )}

          {section === "holidays" && (
            <div className="bg-white border border-stone-200 rounded-xl shadow-soft p-6 space-y-5" data-testid="section-holidays">
              {/* Header with enforcement toggle */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-warmth-100 text-warmth-500 flex items-center justify-center shrink-0">
                    <CalendarDays className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="font-display font-semibold text-stone-900 text-lg">Holidays</h2>
                    <p className="text-sm text-stone-500 mt-1">Block treatment scheduling on holidays you observe. Updates use your country setting (<b>{prefs.country}</b>) and any custom dates you add below.</p>
                  </div>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer shrink-0" data-testid="holiday-enforcement-toggle">
                  <span className="text-sm font-medium" style={{ color: prefs.holiday_enforcement ? "var(--text)" : "var(--text-muted)" }}>
                    Enforcement {prefs.holiday_enforcement ? "on" : "off"}
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={prefs.holiday_enforcement}
                    onClick={() => setEnforcement(!prefs.holiday_enforcement)}
                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                    style={{ background: prefs.holiday_enforcement ? "var(--primary)" : "#D6D3D1" }}
                  >
                    <span
                      className="inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform"
                      style={{ transform: prefs.holiday_enforcement ? "translateX(22px)" : "translateX(2px)" }}
                    />
                  </button>
                </label>
              </div>

              {/* Default holidays */}
              {(() => {
                const countryKey = prefs.country === "United Kingdom" ? "UK" : (["USA", "India", "UK"].includes(prefs.country) ? prefs.country : "USA");
                const countryNote = ["USA", "India", "United Kingdom", "UK"].includes(prefs.country) ? "" : ` (${prefs.country} not yet supported — showing USA defaults)`;
                const calendar = holidayCalendar?.countries?.[countryKey];
                const year = String(new Date().getFullYear());
                const years = calendar?.holidays ? Object.keys(calendar.holidays).sort() : [];
                const useYear = years.includes(year) ? year : years[0];
                const list = (calendar?.holidays?.[useYear] || []);
                const selected = prefs.selected_holiday_names || [];

                return (
                  <div className="pt-3 border-t border-stone-100">
                    <div className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-3">
                      Default holidays ({countryKey}) — {useYear || "—"}{countryNote}
                    </div>
                    {!calendar && <div className="text-sm text-stone-500">Loading holiday calendar…</div>}
                    {calendar && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                        {list.map((h) => {
                          const checked = selected.includes(h.name);
                          return (
                            <label
                              key={`${h.date}-${h.name}`}
                              data-testid={`holiday-default-${h.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                checked ? "border-brand-500 bg-brand-50" : "border-stone-200 hover:bg-stone-50"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleHoliday(h.name)}
                                className="mt-0.5 w-4 h-4"
                                style={{ accentColor: "var(--primary)" }}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-stone-900 truncate">{h.name}</div>
                                <div className="text-xs text-stone-500">{new Date(h.observed || h.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}{h.note ? ` · ${h.note}` : ""}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Custom holidays */}
              <div className="pt-4 border-t border-stone-100">
                <div className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-3">Add custom holiday</div>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-2 items-end">
                  <div>
                    <label className="text-xs font-medium text-stone-600 mb-1 block">Name</label>
                    <input
                      data-testid="custom-holiday-name"
                      placeholder="e.g., Hospital Foundation Day"
                      value={customForm.name}
                      onChange={(e) => setCustomForm({ ...customForm, name: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-600 mb-1 block">Date</label>
                    <input
                      data-testid="custom-holiday-date"
                      type="date"
                      value={customForm.date}
                      onChange={(e) => setCustomForm({ ...customForm, date: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                  <label className="inline-flex items-center gap-2 cursor-pointer mb-2 md:mb-3">
                    <input
                      data-testid="custom-holiday-repeat"
                      type="checkbox"
                      checked={customForm.repeat_yearly}
                      onChange={(e) => setCustomForm({ ...customForm, repeat_yearly: e.target.checked })}
                      className="w-4 h-4"
                      style={{ accentColor: "var(--primary)" }}
                    />
                    <span className="text-sm text-stone-700">Repeat yearly</span>
                  </label>
                  <button
                    type="button"
                    data-testid="custom-holiday-add"
                    onClick={addCustomHoliday}
                    disabled={!customForm.name.trim() || !customForm.date}
                    className="inline-flex items-center gap-1 px-4 py-2.5 rounded-lg border border-stone-300 bg-white text-stone-700 font-semibold hover:bg-stone-50 disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4" />Add
                  </button>
                </div>

                {(prefs.custom_holidays || []).length > 0 && (
                  <div className="mt-4 space-y-2">
                    {prefs.custom_holidays.map((c) => (
                      <div key={c.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-stone-200 bg-stone-50" data-testid={`custom-holiday-row-${c.id}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-stone-900 truncate">{c.name}</div>
                          <div className="text-xs text-stone-500">
                            {new Date(c.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                            {c.repeat_yearly ? " · repeats yearly" : ""}
                          </div>
                        </div>
                        <button
                          type="button"
                          data-testid={`custom-holiday-remove-${c.id}`}
                          onClick={() => removeCustomHoliday(c.id)}
                          className="p-2 rounded-lg text-red-600 hover:bg-red-50 border border-red-200"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {holidayMsg.text && <Toast kind={holidayMsg.kind}>{holidayMsg.text}</Toast>}

              <div className="pt-4 border-t border-stone-100 flex justify-end">
                <button
                  type="button"
                  data-testid="save-holiday-prefs"
                  onClick={saveHolidayPrefs}
                  disabled={!holidayDirty}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />Save Holiday Preferences
                </button>
              </div>
            </div>
          )}

          {section === "appearance" && (
            <div className="bg-white border border-stone-200 rounded-xl shadow-soft p-6 space-y-4 max-w-xl" data-testid="section-appearance">
              <div>
                <h2 className="font-display font-semibold text-stone-900 text-lg">Appearance</h2>
                <p className="text-sm text-stone-500 mt-1">Switch between visual themes. Saved to your device.</p>
              </div>
              <button onClick={()=>setThemeOpen(true)} data-testid="open-theme-picker-from-profile" className="w-full inline-flex items-center gap-3 px-4 py-3 rounded-xl border border-stone-200 hover:bg-stone-50 text-left">
                <Palette className="w-5 h-5 text-brand-500" />
                <div className="flex-1">
                  <div className="font-semibold text-stone-900">Theme</div>
                  <div className="text-xs text-stone-500">Current: {current?.name}</div>
                </div>
                <span className="text-xs text-stone-500">Change</span>
              </button>
              {themeOpen && <ThemePickerModal onClose={()=>setThemeOpen(false)} />}
            </div>
          )}

          {section === "notifications" && (
            <div data-testid="section-notifications">
              <NotificationsPanel />
            </div>
          )}

          {section === "feedback" && (
            <form onSubmit={submitFeedback} className="bg-white border border-stone-200 rounded-xl shadow-soft p-6 space-y-4 max-w-xl" data-testid="section-feedback">
              <div>
                <h2 className="font-display font-semibold text-stone-900 text-lg">Send us feedback</h2>
                <p className="text-sm text-stone-500 mt-1">Your message will be emailed to the application owner. We read everything.</p>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Feedback type*</label>
                <select data-testid="fb-type" value={fb.feedback_type} onChange={(e)=>setFb({...fb, feedback_type:e.target.value})} className={inputCls}>
                  {FEEDBACK_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Subject (optional)</label>
                <input data-testid="fb-subject" value={fb.subject} onChange={(e)=>setFb({...fb, subject:e.target.value})} className={inputCls} placeholder="Short summary" />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Message*</label>
                <textarea data-testid="fb-message" required rows={5} value={fb.message} onChange={(e)=>setFb({...fb, message:e.target.value})} className={inputCls} placeholder="Tell us what's on your mind…" />
              </div>
              {fbMsg.text && <Toast kind={fbMsg.kind}>{fbMsg.text}</Toast>}
              <div className="flex justify-end pt-2">
                <button data-testid="fb-submit-btn" disabled={fbSaving} type="submit" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold disabled:opacity-60">
                  <MessageSquare className="w-4 h-4" />{fbSaving ? "Sending…" : "Send feedback"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
